import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ScriptCoPreview from "./ScriptCoPreview";

const extractFileList = (dataProp) => {
  if (Array.isArray(dataProp)) {
    return dataProp.filter((item) => item && typeof item === "object");
  }
  if (dataProp && typeof dataProp === "object") {
    return Object.values(dataProp).filter(
      (item) =>
        item &&
        typeof item === "object" &&
        (item.fileName || item.name || item.id || item.base64),
    );
  }
  return [];
};

const ALLOWED_EXTENSIONS = new Set(["pdf", "png", "jpg", "jpeg", "gif"]);

const processFile = (file) => {
  return new Promise((resolve, reject) => {
    const extension = file.name.split(".").pop().toLowerCase();

    if (!ALLOWED_EXTENSIONS.has(extension)) {
      reject(
        new Error(
          `Invalid format (${file.name}). Allowed: PDF, PNG, JPG, JPEG, GIF.`,
        ),
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      reject(new Error(`File size exceeds 10MB limit (${file.name}).`));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = reader.result.split(",")[1];
      const fileObj = {
        id: `file_${Date.now()}_${crypto.randomUUID()}`,
        file,
        fileName: file.name,
        fileType: extension,
        size: file.size,
        base64: base64Data,
        mimeType:
          file.type ||
          (extension === "pdf" ? "application/pdf" : `image/${extension}`),
      };

      resolve(fileObj);
    };
    reader.onerror = () =>
      reject(new Error(`Failed to read file ${file.name}`));
    reader.readAsDataURL(file);
  });
};

const isRemoteOrBlobUrl = (str) =>
  str.startsWith("blob:") ||
  str.startsWith("http://") ||
  str.startsWith("https://");

const inferMimeTypeFromName = (lowerName) => {
  if (lowerName.endsWith(".pdf")) return "application/pdf";
  if (lowerName.endsWith(".png")) return "image/png";
  if (lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (lowerName.endsWith(".gif")) return "image/gif";
  return "application/pdf";
};

const resolveEffectiveMimeType = (mimeType, fileName) => {
  if (mimeType && mimeType !== "application/octet-stream") return mimeType;
  return inferMimeTypeFromName((fileName || "").toLowerCase());
};

const rawPdfTextToBlobUrl = (text) => {
  const byteArray = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    byteArray[i] = text.codePointAt(i) & 0xff;
  }
  const blob = new Blob([byteArray], { type: "application/pdf" });
  return URL.createObjectURL(blob);
};

const base64ToBlobUrl = (base64, mimeType) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.codePointAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return URL.createObjectURL(blob);
};

const fallbackPreviewSource = (base64Str, fileName) => {
  if (typeof base64Str === "string" && !base64Str.startsWith("data:")) {
    const lowerName = (fileName || "").toLowerCase();
    const type = lowerName.endsWith(".pdf") ? "application/pdf" : "image/png";
    return `data:${type};base64,${base64Str}`;
  }
  return base64Str;
};

const createBlobUrl = (base64Str, mimeType, fileName) => {
  if (!base64Str) return null;
  try {
    let cleanStr = String(base64Str).trim();

    if (isRemoteOrBlobUrl(cleanStr)) {
      return cleanStr;
    }

    let effectiveMimeType = resolveEffectiveMimeType(mimeType, fileName);

    if (cleanStr.startsWith("data:")) {
      const parts = cleanStr.split(",");
      const mimeMatch = /data:(.*?);/.exec(parts[0]);
      if (mimeMatch) effectiveMimeType = mimeMatch[1];
      cleanStr = parts[1] || "";
    }

    if (cleanStr.startsWith("%PDF")) {
      return rawPdfTextToBlobUrl(cleanStr);
    }

    return base64ToBlobUrl(cleanStr, effectiveMimeType);
  } catch (e) {
    console.error("Error creating blob URL for preview:", e);
    return fallbackPreviewSource(base64Str, fileName);
  }
};

const resolvePreviewUrl = (fileObj) => {
  if (fileObj.file) {
    return URL.createObjectURL(fileObj.file);
  }
  const rawData = fileObj.base64 || fileObj.content || fileObj.data;
  if (rawData) {
    return createBlobUrl(
      rawData,
      fileObj.mimeType || fileObj.type,
      fileObj.fileName || fileObj.name,
    );
  }
  return fileObj.url || fileObj.fileUrl || fileObj.path;
};

const getDropzoneSx = (disabled, hasError, background) => ({
  border: hasError ? "1.5px dashed #d32f2f" : "1.5px dashed #403C6F",
  borderRadius: "8px",
  backgroundColor: background,
  py: 1.75,
  px: 2.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: disabled ? "#F2F2F2" : "rgba(64, 60, 111, 0.04)",
    borderColor: "#403C6F",
  },
});

const ScriptCoRequiredDocuments = ({
  data,
  onDataChange,
  onValidationChange,
  onFilesChange,
  customerId,
  showErrors,
  disabled = false,
}) => {
  const fileInputRef = useRef(null);
  const [error, setError] = useState("");
  const [localFiles, setLocalFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Computed display list: uses local state if present, fallback to data prop
  const parentFiles = extractFileList(data);
  const displayList = localFiles.length > 0 ? localFiles : parentFiles;

  const isValidDoc = displayList.length > 0;

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValidDoc);
    }
  }, [isValidDoc, onValidationChange]);

  const updateFiles = (newList) => {
    setLocalFiles(newList);
    if (onDataChange) {
      onDataChange(newList);
    }
    if (onFilesChange) {
      const fileMap = {};
      newList.forEach((fileObj, idx) => {
        fileMap[`file_${idx}`] = fileObj;
      });
      onFilesChange(fileMap);
    }
  };

  const handleFilesSelect = async (files) => {
    if (!files || files.length === 0) return;
    setError("");
    setIsUploading(true);

    const results = await Promise.allSettled(
      Array.from(files).map(processFile),
    );
    const newUploaded = results
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value);
    const errors = results
      .filter((result) => result.status === "rejected")
      .map((result) => result.reason.message);

    if (errors.length > 0) {
      setError(errors.join(" "));
    }

    if (newUploaded.length > 0) {
      const updatedList = [...displayList, ...newUploaded];
      updateFiles(updatedList);
    }

    setIsUploading(false);
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      void handleFilesSelect(Array.from(e.target.files));
    }
    e.target.value = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      void handleFilesSelect(Array.from(e.dataTransfer.files));
    }
  };

  const handleRemove = (id) => {
    const updatedList = displayList.filter(
      (item, idx) => item.id !== id && idx !== id,
    );
    updateFiles(updatedList);
  };

  const handleOpenPreview = (fileObj) => {
    setPreviewFile(fileObj);
    const url = resolvePreviewUrl(fileObj);
    if (url !== undefined) {
      setPreviewUrl(url);
    }
  };

  const handleClosePreview = () => {
    setPreviewFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const hasDropzoneError = error || (showErrors && displayList.length === 0);
  let dropzoneBackground = "#F9FAFB";
  if (disabled) {
    dropzoneBackground = "#F2F2F2";
  } else if (hasDropzoneError) {
    dropzoneBackground = "#FFF5F5";
  }

  const dropzoneHandlers = disabled
    ? {}
    : {
        onDragOver: handleDragOver,
        onDrop: handleDrop,
        onClick: () => fileInputRef.current?.click(),
      };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2.5,
        border: "1px solid #E8E8E9",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        boxSizing: "border-box",
        flex: 1,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography
          sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
        >
          Prescription (Rx)<span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "#66638C" }}>
          Upload one or more prescription documents (PDF, PNG, JPG, JPEG, max
          10MB per file).
        </Typography>
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleInputChange}
        accept=".pdf,.png,.jpg,.jpeg,.gif"
        multiple
      />

      {/* If NO files are uploaded, show the big Drag and Drop Dropzone */}
      {displayList.length === 0 ? (
        <Box
          {...dropzoneHandlers}
          sx={getDropzoneSx(disabled, hasDropzoneError, dropzoneBackground)}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <CloudUploadOutlinedIcon
              sx={{ fontSize: 28, color: "#403C6F", flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#0e1324",
              }}
            >
              {isUploading
                ? "Processing files..."
                : "Drag and drop your files here to select multiple documents"}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={disabled || isUploading}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) fileInputRef.current?.click();
            }}
            sx={{
              backgroundColor: "#403C6F",
              color: "#FFF",
              borderRadius: "50px",
              textTransform: "none",
              px: 2.5,
              py: 0.6,
              fontWeight: 500,
              fontSize: "13px",
              boxShadow: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#312e5e",
                boxShadow: "none",
              },
            }}
          >
            Select Documents
          </Button>
        </Box>
      ) : (
        /* When AT LEAST ONE file is uploaded, replace the big box with the list of uploaded documents & single button below */
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography
              sx={{ fontSize: "13px", fontWeight: 600, color: "#403C6F" }}
            >
              Uploaded Documents ({displayList.length})
            </Typography>
            {displayList.map((fileObj, idx) => (
              <Box
                key={fileObj.id || `file_${idx}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #E8E8E9",
                  borderRadius: "8px",
                  p: 1.5,
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    minWidth: 0,
                  }}
                >
                  <InsertDriveFileOutlinedIcon
                    sx={{ fontSize: 28, color: "#403C6F", flexShrink: 0 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#0e1324",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {fileObj.fileName ||
                        fileObj.name ||
                        `Document ${idx + 1}`}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#66638C" }}>
                      {formatFileSize(fileObj.size)}{" "}
                      {fileObj.fileType
                        ? `• ${fileObj.fileType.toUpperCase()}`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleOpenPreview(fileObj)}
                    title="Preview Document"
                    sx={{
                      color: "#403C6F",
                      "&:hover": { backgroundColor: "rgba(64, 60, 111, 0.08)" },
                    }}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(fileObj.id || idx)}
                    disabled={disabled}
                    title="Remove Document"
                    sx={{
                      color: disabled ? "#ccc" : "#d32f2f",
                      "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.08)" },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>

          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isUploading}
            startIcon={<CloudUploadOutlinedIcon />}
            variant="outlined"
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "13px",
              color: "#403C6F",
              borderColor: "#403C6F",
              borderRadius: "50px",
              px: 2.5,
              py: 0.75,
              "&:hover": {
                backgroundColor: "rgba(64, 60, 111, 0.04)",
                borderColor: "#312e5e",
              },
            }}
          >
            Upload Document
          </Button>
        </Box>
      )}

      {(error || (showErrors && displayList.length === 0)) && (
        <Typography
          sx={{ color: "#d32f2f", fontSize: "12px", fontWeight: 500, mt: -0.5 }}
        >
          {error || "At least one Prescription (Rx) document is required."}
        </Typography>
      )}

      {/* Preview Dialog */}
      {previewFile && (
        <Dialog
          open={Boolean(previewFile)}
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: "16px",
                p: 2,
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 1,
              fontWeight: 700,
              fontSize: "18px",
              color: "#10100F",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Prescription (Rx) Document Preview</span>
            <IconButton
              onClick={handleClosePreview}
              size="small"
              sx={{ color: "#9F9DB6", "&:hover": { color: "#E53935" } }}
            >
              <CloseIcon sx={{ fontSize: "20px" }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 1, mt: 1 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#403C6F",
                mb: 1.5,
              }}
            >
              File:{" "}
              <span style={{ fontWeight: 400, color: "#10100F" }}>
                {previewFile.fileName || previewFile.name}
              </span>
            </Typography>
            <ScriptCoPreview
              fileUrl={previewUrl}
              fileName={previewFile.fileName || previewFile.name}
            />
          </DialogContent>
          <DialogActions sx={{ p: 1 }}>
            <Button
              onClick={handleClosePreview}
              variant="contained"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                backgroundColor: "#403C6F",
                color: "#FFF",
                px: 4,
                py: 1,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#312e5e", boxShadow: "none" },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

ScriptCoRequiredDocuments.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  onFilesChange: PropTypes.func,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showErrors: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default ScriptCoRequiredDocuments;
